'use client'

import { WithClassname } from "@/@types/defaultProps"
import {PropsWithChildren, useRef, useEffect, useState} from "react"
import { createPortal } from "react-dom"
import AppIcon from "@/app/components/atomic/AppIcon";
import useOnClickOutside from "@/app/hooks/useOnClickOutside";
import useDelayedMount from "@/app/hooks/useDelayedMount";
import useBlockScroll from "@/app/hooks/useBlockScroll";
import SectionTitle, {SectionTitleTheme} from "@/app/components/atomic/molecules/SectionTitle";
import {getThemeColors, ThemeCocoon} from "@/app/constants/theme";

export interface IModal extends PropsWithChildren {
    isOpen?: boolean;
    closeModal?: Function;
    modalName: string;
    fullHeightMobile?: boolean;
    modalTitle?: string;
}

function Modal(props: IModal) {
    const { isOpen = false, fullHeightMobile = false, modalTitle } = props
    const [mounted, setMounted] = useState(false)

    const modalRef = useRef<HTMLDivElement|null>(null);
    useOnClickOutside(modalRef, () => {
        props.closeModal?.(props.modalName)
    })
    useBlockScroll(isOpen)
    const isFullyOpen = useDelayedMount(isOpen, 200)
    const layoutClasses = "flex flex-col"
    const contentClasses = fullHeightMobile ? "rm-md:px-[35px] rm-md:py-[25px]" : "px-[35px] py-[25px]"
    const themeClasses = "bg-white text-pale"
    const openClasses = isFullyOpen
        ? "rm-md:right-0 max-rm-md:bottom-0 opacity-1"
        : "rm-md:-right-80 -max-rm-md:bottom-80 opacity-0"
    const responsiveClasses = `w-[650px] max-rm-md:w-full max-rm-md:left-0 h-dvh max-rm-md:${fullHeightMobile ? 'h-dvh w-full md:w-[650px]' : 'h-[90%]'} bottom-0 max-rm-md:bottom-0`;

    useEffect(() => {
        setMounted(true)
    }, [])

    if (!mounted || !isOpen) return null

    const modalContent = <div className={"w-screen h-dvh fixed z-[50]"}>
        {isFullyOpen && <div className="w-full h-full fixed z-[50] top-0 left-0 bg-blue-400/20 backdrop-blur-sm"></div>}
        {isOpen && <CloseButton
            {...props}
            isOpen={isFullyOpen}
            closeModal={() => props.closeModal?.(props.modalName)}
        />}
        <div
            ref={modalRef}
            className={`shadow-2xl transition-all overflow-y-auto duration-300 ease-in-out ${responsiveClasses} fixed ${openClasses} z-50 ${themeClasses} ${!fullHeightMobile ? contentClasses : ''} ${layoutClasses}`}
            data-test="callback-modal"
            role="dialog"
        >
            {fullHeightMobile && (
                <div className="max-rm-md:flex rm-md:hidden inline-flex flex-row items-center justify-between w-full bg-white p-rm-25 border-b border-gray-200">
                    <span className="text-rm-16 font-medium text-pale">{modalTitle || props.modalName}</span>
                    <button
                        onClick={() => props.closeModal?.(props.modalName)}
                        className="text-pale size-[35px] border border-blue-200 rounded-rm-6"
                    >
                        <AppIcon iconClassName={"fa-times"} className={"font-bold"}/>
                    </button>
                </div>
            )}
            <div className={fullHeightMobile ? "px-[35px] py-[25px] flex-1" : ""}>
                {props.children}
            </div>
        </div>
    </div>

    return createPortal(modalContent, document.body)
}

function CloseButton(props: IModal) {
    const responsiveClasses = `hidden md:flex bottom-[calc(90%-1.5rem)] rm-md:bottom-1/2 rm-md:-translate-y-1/2 rm-md:right-[650px] max-rm-md:left-1/2 ${props.isOpen ? 'rm-md:mr-10' : 'rm-md:mr-0'} max-rm-md:-translate-x-1/2`
    return <button
        type="button"
        onClick={() => props.closeModal?.()}
        id={"close-button"}
        aria-label="Fermer"
        className={`transition-all z-[51] duration-500 outline outline-1 shadow-lg outline-blue-200 fixed ${responsiveClasses} bg-blue-100 hover:bg-blue-200 cursor-pointer rounded-full w-12 h-12 flex justify-center items-center`}
    >
        <AppIcon iconClassName={"fa-times"} className={"text-blue-700 font-bold"}/>
    </button>
}

Modal.Title = function Title(props: PropsWithChildren & WithClassname & { productType?: ThemeCocoon }) {
    return <SectionTitle
        level="h2"
        theme={getThemeColors(props.productType).primary as SectionTitleTheme}
    >
        {props.children}
    </SectionTitle>
}

Modal.Paragraph = function Paragraph(props: PropsWithChildren & WithClassname) {
    return <p className={props.className ?? ''}>{props.children}</p>
}

export default Modal
